<template>
  <span :class="pillClass" @keydown.enter="$emit('keydownEnter')">
    <slot />
  </span>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

/** Allowed `color` prop values (string values match pill modifier keys). */
/* eslint-disable no-unused-vars -- enum members are the public color API */
export enum PillColor {
  red = 'red',
  yellow = 'yellow',
  green = 'green',
  blue = 'blue',
  brand = 'brand',
  grey = 'grey',
  black = 'black',
}
/* eslint-enable no-unused-vars */

const PILL_CLASS_BY_COLOR: Record<PillColor, string> = {
  [PillColor.red]: 'pill--red',
  [PillColor.yellow]: 'pill--yellow',
  [PillColor.green]: 'pill--green',
  [PillColor.blue]: 'pill--blue',
  [PillColor.brand]: 'pill--brand',
  [PillColor.grey]: 'pill--grey',
  [PillColor.black]: 'pill--black',
};

export default defineComponent({
  name: 'Pill',
  props: {
    color: {
      type: String as PropType<PillColor>,
      required: true,
      validator: (v: string) => (Object.values(PillColor) as string[]).includes(v),
    },
  },
  computed: {
    pillClass(): string {
      return `pill ${PILL_CLASS_BY_COLOR[this.color]}`;
    },
  },
});
</script>

<style lang="scss" scoped>
.pill {
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.09px;
  padding: 0.25rem 0.75rem;
  text-align: center;

  &--red {
    background: theme('colors.critical.subdued'); // theme.critical.subdued
    color: theme('colors.critical.contrast');
  }

  &--yellow {
    background: theme('colors.warning.subdued');
    color: theme('colors.warning.contrast');
  }

  &--green {
    background: theme('colors.success.subdued');
    color: theme('colors.success.contrast');
  }

  &--blue {
    background: theme('colors.safety.subdued');
    color: theme('colors.safety.contrast');
  }

  &--brand {
    background: theme('colors.primary.subdued');
    color: theme('colors.primary.DEFAULT');
  }

  &--grey {
    background: theme('colors.grey.divider.subdued');
    color: theme('colors.grey.header');
  }

  &--black {
    color: theme('colors.grey.background.subdued');
    background: theme('colors.grey.header');
  }
}
</style>
